import React, { memo, useCallback, useMemo, useState } from 'react';
import { Box, Image, Skeleton } from '@betty/moonlight';
import { RegistrationOfferImageTypes } from './RegistrationOfferImage.types';
import { PromotionRewardTarget } from './RegistrationOfferImage.constants';

const imageLoadCache = new Map<string, boolean>();

function RegistrationOfferImage({
  rewards,
  name,
  miniImageUrl,
  ...rest
}: RegistrationOfferImageTypes) {
  const { variant, gradient, ...boxProps } = rest;
  const cacheKey = useMemo(() => {
    return miniImageUrl || (name ? `promos.${name}` : '');
  }, [miniImageUrl, name]);

  const [loaded, setLoaded] = useState(() => !!imageLoadCache.get(cacheKey));
  const existing = name || miniImageUrl;

  const onLoad = useCallback(() => {
    setLoaded(true);
    if (cacheKey) {
      imageLoadCache.set(cacheKey, true);
    }
  }, [cacheKey]);

  const imageName = useMemo(() => {
    const isBundleReward = rewards?.length > 1;
    const [reward] = rewards;
    if (
      isBundleReward ||
      reward.rewardTarget === PromotionRewardTarget.FreeSpins
    ) {
      return undefined;
    } else if (
      reward.rewardTarget === PromotionRewardTarget.Cash ||
      reward.rewardTarget === PromotionRewardTarget.Bonus
    ) {
      return 'cashOrBonus';
    } else if (reward.rewardTarget === PromotionRewardTarget.Coins) {
      return 'bettyPromoCoins';
    } else if (reward.rewardTarget === PromotionRewardTarget.InstantWheel) {
      return 'bettyPromoWheel';
    } else if (miniImageUrl) {
      return 'fallBackBannerMini';
    }
  }, [rewards, miniImageUrl]);

  return (
    <Box {...boxProps}>
      {!loaded && existing ? (
        <Box
          position="absolute"
          top="space-0"
          left="space-0"
          width="100%"
          height="100%"
          zIndex={1}
        >
          <Skeleton
            borderWidth="border-width-050"
            borderColor="borderPrimary"
            borderRadius="radius-200"
            width="100%"
            height="100%"
          />
        </Box>
      ) : null}
      {existing ? (
        <Image
          name={imageName && `promos.${imageName}`}
          source={miniImageUrl}
          onLoad={onLoad}
          contentFit="contain"
          borderWidth="border-width-050"
          borderColor="borderPrimary"
          borderRadius="radius-400"
          priority="high"
          cachePolicy="memory-disk"
          width="100%"
          height="100%"
          opacity={loaded ? 1 : 0}
        />
      ) : null}
    </Box>
  );
}

export default memo(RegistrationOfferImage);
